const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// The backend serves one synced snapshot, so a GET answered once stays correct for this
// session. Revisiting a page is then instant instead of recomputing and re-downloading.
const CACHE = new Map();

export const clearApiCache = () => CACHE.clear();

async function get(path, { cache = true } = {}) {
  if (cache && CACHE.has(path)) return CACHE.get(path);
  const promise = (async () => {
    const res = await fetch(`${API}${path}`);
    if (!res.ok) {
      const body = await res.text();
      throw new Error(`${res.status} ${body.slice(0, 200)}`);
    }
    return res.json();
  })();
  if (cache) {
    CACHE.set(path, promise);
    promise.catch(() => CACHE.delete(path));
  }
  return promise;
}

export const getMeta = () => get("/meta");
export const getDatasets = () => get("/datasets");
export const getContextLibrary = () => get("/context-library");
export const getDataReality = () => get("/data-reality");
export const getRegistry = () => get("/metrics/registry");
export const getSamplePrompts = () => get("/sample-prompts");
export const getIndicators = () => get("/indicators");
export const getAudit = (limit = 50) => get(`/audit?limit=${limit}`, { cache: false });
export const getAuditDetail = (id) => get(`/audit/${id}`);

export const getPartners = (params = {}) => get(`/partners${qs(params)}`);
export const getPartnerDetail = (name, params = {}) =>
  get(`/partners/${encodeURIComponent(name)}${qs(params)}`);
export const getReallocation = (params = {}) => get(`/reallocation${qs(params)}`);
export const getBenchmark = (params = {}) => get(`/benchmark${qs(params)}`);
export const getBenchmarkGrid = (params = {}) => get(`/benchmark/grid${qs(params)}`);
export const getBenchmarkMcaBasis = (params = {}) => get(`/benchmark/mca-basis${qs(params)}`);
export const getNeedIndexState = (params = {}) => get(`/need-index/state${qs(params)}`);
export const getStateBrief = (state) => get(`/brief/state${qs({ state })}`);
export const getBoardNote = () => get("/board-note");
export const getDistrictProfile = (state, district) => get(`/district${qs({ state, district })}`);
export const getNeedIndexDistrict = (params = {}) => get(`/need-index${qs(params)}`);

export async function ask(question, sessionId, intent) {
  const res = await fetch(`${API}/ask`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ question, session_id: sessionId, intent: intent || null }),
  });
  if (!res.ok) throw new Error(`ask failed: ${res.status} ${await res.text()}`);
  return res.json();
}

export function streamNarration(answerId, onChunk, onDone, onError) {
  const es = new EventSource(`${API}/ask/${answerId}/narrate`);
  es.onmessage = (e) => {
    if (e.data === "[DONE]") {
      es.close();
      onDone?.();
      return;
    }
    onChunk(e.data.split("<<NL>>").join("\n"));
  };
  es.onerror = () => {
    es.close();
    onError?.();
  };
  return () => es.close();
}

function qs(params) {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "" ) return;
    if (Array.isArray(v)) v.forEach((x) => sp.append(k, x));
    else sp.append(k, v);
  });
  const s = sp.toString();
  return s ? `?${s}` : "";
}
